import i18n from "@/i18n";
import { cn } from "@/lib/cn";
import { NodeStatus as NS } from "@shared/types";
import type { LogChunk } from "@shared/types";
import { Status } from "@shared/types/models";
import { buildScrapingTree, getNodeStatus } from "@shared/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AddressNode } from "./AddressNode";
import * as styles from "./LiveLogTree.styles";
import type { AddressNodeData, LiveLogTreeProps, LogEntry } from "./types";
import { LogLevel as LL } from "./types";

const getStatusLabel = (status: NS): string => {
  switch (status) {
    case NS.LOADING:
      return i18n.t("scraping:loading");
    case NS.SUCCESS:
      return i18n.t("scraping:finished");
    case NS.ERROR:
      return i18n.t("scraping:failed");
    case NS.PENDING:
      return i18n.t("scraping:pending");
    case NS.WARN:
      return i18n.t("scraping:warning");
    default:
      return status;
  }
};

const getTimestamp = (ts?: number): string => {
  const date = ts ? new Date(ts) : new Date();
  const locale = i18n?.language || "bs-Latn-BA";
  return date.toLocaleTimeString(locale, {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  });
};

const initializeProviderStates = (
  initialAddresses: NonNullable<LiveLogTreeProps["initialAddresses"]>,
  states: Map<string, { status: NS; entries: LogEntry[] }>,
): void => {
  for (const addr of initialAddresses) {
    for (const provider of addr.providers ?? []) {
      const key = `prov-${addr.addressName}-${provider.name}`;
      states.set(key, { status: NS.LOADING, entries: [] });
    }
  }
};

const processSingleRawLog = (log: LogChunk, newStates: Map<string, { status: NS; entries: LogEntry[] }>): void => {
  const { address: addressName, provider: providerName } = log;

  if (!addressName || !providerName) return;

  const key = `prov-${addressName}-${providerName}`;

  const newStatus = getNodeStatus(log.status);
  const newLevel: LL = log.status === Status.FAILED ? LL.ERROR : LL.INFO;

  let existingState = newStates.get(key);
  if (!existingState) existingState = { status: NS.LOADING, entries: [] };

  newStates.set(key, {
    status: newStatus,
    entries: [
      ...(existingState.entries || []),
      { level: newLevel, timestamp: getTimestamp(log.ts), message: log.message || log.status, jobId: key, key: log.key, params: log.params },
    ],
  });
};

const buildAddressData = (
  initialAddresses: NonNullable<LiveLogTreeProps["initialAddresses"]>,
  providerStates: Map<string, { status: NS; entries: LogEntry[] }>,
): AddressNodeData[] => {
  const statuses = new Map([...providerStates].map(([id, state]) => [id, state.status]));
  return buildScrapingTree(initialAddresses, statuses).map((addr) => ({
    ...addr,
    label: getStatusLabel(addr.status),
    entries: [],
    providers: addr.providers.map((prov) => ({ ...prov, label: getStatusLabel(prov.status), entries: providerStates.get(prov.id)?.entries ?? [] })),
  }));
};

const LiveLogTree = ({ initialAddresses, rawLogs, className }: LiveLogTreeProps) => {
  const { t } = useTranslation("scraping");
  const logs = rawLogs ?? [];

  const providerStates = useMemo(() => {
    const states = new Map<string, { status: NS; entries: LogEntry[] }>();
    if (!initialAddresses?.length) return states;

    initializeProviderStates(initialAddresses, states);
    logs.forEach((log) => processSingleRawLog(log, states));

    return states;
  }, [initialAddresses, logs]);

  const addressData = useMemo(() => {
    if (!initialAddresses?.length) return [] as AddressNodeData[];
    return buildAddressData(initialAddresses, providerStates);
  }, [initialAddresses, providerStates]);

  if (!addressData.length) {
    return <div className={styles.waiting}>{t("waitingForLogs")}</div>;
  }

  return (
    <div className={cn(styles.container, className)}>
      {addressData.map((addr) => (
        <AddressNode key={addr.addressName} {...addr} />
      ))}
    </div>
  );
};

export { LiveLogTree };
